import React from "react";
import { Card, CardContainer } from "../../../components/Card";
import { Box, Typography, useTheme } from "@mui/material";
import {
    BarChart,
    HorizontalBarChart,
    PieChart,
} from "../../../components/Charts";
import FollowersSlider from "../../../components/FollowersSlider";

function Audience({ data, platform }) {
    const theme = useTheme();

    const getTopValue = (dataArray) => {
        return dataArray?.sort((a, b) => b.value - a.value)[0];
    };

    const topGender = getTopValue(data?.audience?.gender_distribution);
    const topCountry = getTopValue(data?.audience?.countries);
    const topLanguage = getTopValue(data?.audience?.languages);
    const topAgeGroup = getTopValue(data?.audience?.gender_age_distribution);

    const youtubeMetrics = [
        {
            name: "Top Gender",
            value: topGender?.gender,
            percentage: topGender?.value,
        },
        {
            name: "Top Country",
            value: topCountry?.name,
            percentage: topCountry?.value,
        },
        {
            name: "Top Language",
            value: topLanguage?.name,
            percentage: topLanguage?.value,
        },
        {
            name: "Top Age Group",
            value: topAgeGroup?.age_range,
            percentage: topAgeGroup?.value,
        },
    ];

    const colorMapping = {
        "Top Gender": theme.palette.graph.cyan,
        "Top Country": theme.palette.graph.yellow,
        "Top Language": theme.palette.graph.magenta,
        "Top Age Group": theme.palette.graph.purple,
        mass_followers: theme.palette.graph.cyan,
        suspicious: theme.palette.graph.yellow,
        influencers: theme.palette.graph.magenta,
        real: theme.palette.graph.purple,
    };

    const transformedData = [];
    const ageRanges = {};

    data?.audience?.gender_age_distribution?.forEach((entry) => {
        const { age_range, gender, value } = entry;
        if (!ageRanges[age_range]) {
            ageRanges[age_range] = { age_range };
        }
        ageRanges[age_range][gender] = value;
    });

    for (const values of Object.values(ageRanges)) {
        transformedData.push(values);
    }
    const transformedDataGender_distribution =
        data?.audience?.gender_distribution.map((item) => ({
            male_value: item.gender === "MALE" ? item.value : 0,
            female_value: item.gender === "FEMALE" ? item.value : 0,
        }));

    function processLanguageData(data) {
        const sortedData = [...data].sort((a, b) => b.value - a.value);
        const topItems = sortedData.slice(0, 5);
        let otherValue = sortedData.slice(5).reduce((sum, item) => {
            if (item.name === "Other") {
                return sum + item.value;
            }
            return sum + item.value;
        }, 0);

        const existingOtherIndex = topItems.findIndex(
            (item) => item.name === "Other"
        );
        if (existingOtherIndex !== -1) {
            otherValue += topItems[existingOtherIndex].value;
            topItems.splice(existingOtherIndex, 1);
        }

        topItems.push({ code: "other", name: "Other", value: otherValue });
        return topItems;
    }

    const processedData = processLanguageData(data?.audience?.languages);
    const series = processedData.map((item) => ({
        key: item.code,
        name: item.name,
    }));

    function processCityData(data) {
        const sortedData = [...data].sort((a, b) => b.value - a.value);
        const topItems = sortedData.slice(0, 5);
        const otherValue = sortedData
            .slice(5)
            .reduce((sum, item) => sum + item.value, 0);
        topItems.push({ name: "Other", value: otherValue });
        return topItems;
    }

    const processedCityData = processCityData(data?.audience?.cities);
    const processedCountryData = processCityData(data?.audience?.countries);
    const processedInterestsData = processCityData(data?.audience?.interests);

    return (
        <>
            <CardContainer>
                {platform === "youtube"
                    ? youtubeMetrics.map((metric) => (
                          <Card key={metric.name}>
                              <Typography
                                  variant="h5"
                                  sx={{
                                      color:
                                          colorMapping[metric.name] ||
                                          theme.palette.graph.cyan,
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                  }}
                              >
                                  {metric.value}
                              </Typography>
                              <Typography
                                  variant="captionRegular"
                                  sx={{
                                      color: theme.palette.neutral[500],
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                  }}
                              >
                                  {metric.name}
                                  {/* ({(metric.percentage * 100).toFixed(2)}%) */}
                              </Typography>
                          </Card>
                      ))
                    : data?.audience?.follower_types?.map((followerType) => (
                          <Card key={followerType.name}>
                              <Typography
                                  variant="h5"
                                  sx={{
                                      color:
                                          colorMapping[followerType.name] ||
                                          theme.palette.graph.cyan,
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                  }}
                              >
                                  {followerType.value.toFixed(2)}%
                              </Typography>
                              <Typography
                                  variant="captionRegular"
                                  sx={{
                                      color: theme.palette.neutral[500],
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                  }}
                              >
                                  {followerType.name
                                      .replace("_", " ")
                                      .replace(/\b\w/g, (char) =>
                                          char.toUpperCase()
                                      )}
                              </Typography>
                          </Card>
                      ))}
            </CardContainer>
            <CardContainer>
                <div style={{ display: "flex" }}>
                    <PieChart
                        data={transformedDataGender_distribution}
                        series={[
                            { name: "MALE", key: "male_value" },
                            { name: "FEMALE", key: "female_value" },
                        ]}
                        title="Gender Distribution"
                    />
                </div>
                <BarChart
                    data={transformedData}
                    series={[
                        { name: "MALE", key: "MALE" },
                        { name: "FEMALE", key: "FEMALE" },
                    ]}
                    xKey="age_range"
                    title="Audience Demographics"
                    isCategoryX={true}
                />
            </CardContainer>
            <CardContainer>
                <HorizontalBarChart
                    data={processedCountryData}
                    series={[{ key: "value", name: "Value" }]}
                    xLabel="countries"
                    title="Country Values Distribution"
                    xKey="name"
                    isCategoryX={true}
                />
                {platform === "instagram" ? (
                    <HorizontalBarChart
                        data={processedCityData}
                        series={[{ key: "value", name: "Value" }]}
                        xLabel="Cities"
                        title="City Values Distribution"
                        xKey="name"
                        isCategoryX={true}
                    />
                ) : (
                    <PieChart
                        data={processedData}
                        series={series}
                        title="Language Distribution"
                        xKey="code"
                    />
                )}
            </CardContainer>
            {platform === "instagram" && (
                <CardContainer>
                    <div style={{ display: "flex" }}>
                        <PieChart
                            data={processedData}
                            series={series}
                            title="Language Distribution"
                            xKey="code"
                        />
                    </div>

                    <BarChart
                        data={processedInterestsData}
                        series={[{ key: "value", name: "Values" }]}
                        title="Top Audience Interests"
                        xKey="name"
                        isCategoryX={true}
                    />
                </CardContainer>
            )}
            <Box className="no-export">
                <FollowersSlider data={data?.audience?.significant_followers} />
                {platform === "youtube" && (
                    <FollowersSlider
                        data={data?.audience?.lookalikes}
                        containerTitle="Similar Creators"
                    />
                )}
            </Box>
        </>
    );
}

export default Audience;
